/**
 * Service worker source for the AVOS PWA (Requirement 21.2).
 *
 * A registered, active service worker is the second half of install
 * eligibility (the first is the manifest). This module exposes the SW body as
 * a string so a host can serve it verbatim at the scope root (e.g. `/sw.js`)
 * without needing a separate bundler step — the SW runs in a Worker global,
 * not the page, so it cannot be an ES module import from the app graph.
 *
 * The worker implements the minimal, register-able lifecycle:
 *   - `install`: pre-cache the app shell and `skipWaiting()` so a new worker
 *     activates promptly.
 *   - `activate`: drop stale caches and `clients.claim()` so the active worker
 *     controls open pages immediately.
 *   - `fetch`: an app-shell cache — serve navigations from the cached shell
 *     when the network is unavailable (offline app-shell), and cache-first for
 *     the precached static assets, falling back to the network otherwise.
 *
 * The cache name is versioned; bumping {@link SW_CACHE_VERSION} invalidates old
 * caches on the next activate.
 */

/** Cache version — bump to invalidate the app-shell cache on next activate. */
export const SW_CACHE_VERSION = "avos-shell-v2" as const;

/** The conventional path a host serves the worker at (scope = "/"). */
export const SERVICE_WORKER_PATH = "/sw.js" as const;

/**
 * The URLs pre-cached at install time to form the offline app shell. These
 * mirror the manifest's `start_url`/icon assets so a supported browser can
 * launch the installed app offline.
 */
export const APP_SHELL_URLS: readonly string[] = [
  "/",
  "/manifest.webmanifest",
  "/icons/icon-192.png",
  "/icons/icon-512.png",
  "/icons/maskable-512.png",
];

/**
 * The service worker script body, as a string. A host serves this with
 * `Content-Type: text/javascript` at {@link SERVICE_WORKER_PATH}. It is plain
 * worker-global JavaScript (no imports, no bundler needed).
 */
export const SERVICE_WORKER_SOURCE = `/* AVOS service worker — generated app-shell cache (Req 21.2). */
'use strict';

const CACHE_NAME = ${JSON.stringify(SW_CACHE_VERSION)};
const APP_SHELL_URLS = ${JSON.stringify(APP_SHELL_URLS)};

self.addEventListener('install', (event) => {
  event.waitUntil(
    caches
      .open(CACHE_NAME)
      .then((cache) => cache.addAll(APP_SHELL_URLS))
      .then(() => self.skipWaiting())
  );
});

self.addEventListener('activate', (event) => {
  event.waitUntil(
    caches
      .keys()
      .then((keys) =>
        Promise.all(
          keys
            .filter((key) => key !== CACHE_NAME)
            .map((key) => caches.delete(key))
        )
      )
      .then(() => self.clients.claim())
  );
});

self.addEventListener('fetch', (event) => {
  const request = event.request;
  if (request.method !== 'GET') {
    return;
  }

  // App-shell offline fallback for navigations.
  if (request.mode === 'navigate') {
    event.respondWith(
      fetch(request).catch(() =>
        caches.open(CACHE_NAME).then((cache) => cache.match('/'))
      )
    );
    return;
  }

  // The client bundle and API responses must ALWAYS come from the network when
  // available, so a new deploy/build is never shadowed by a stale cached copy.
  // (Falls back to any cached copy only when offline.)
  const url = new URL(request.url);
  if (url.pathname.startsWith('/client/') || url.pathname.startsWith('/api/')) {
    event.respondWith(fetch(request).catch(() => caches.match(request)));
    return;
  }

  // Cache-first for other (static, precached) GETs, falling back to the network.
  event.respondWith(
    caches.match(request).then((cached) => cached || fetch(request))
  );
});
`;
